import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

export type QualityLevel = 'low' | 'medium' | 'high';

interface Preset {
  pixelRatio: number;
  shadows: boolean;
  shadowSize: number;
  bloom: boolean;
  msaa: number;
  grassDensity: number;
}

export const PRESETS: Record<QualityLevel, Preset> = {
  low: { pixelRatio: 1, shadows: false, shadowSize: 1024, bloom: false, msaa: 0, grassDensity: 0.35 },
  medium: { pixelRatio: 1.5, shadows: true, shadowSize: 1024, bloom: true, msaa: 2, grassDensity: 0.7 },
  high: { pixelRatio: 2, shadows: true, shadowSize: 2048, bloom: true, msaa: 4, grassDensity: 1 },
};

/** Picks a starting preset from device hints; the game also adapts at runtime from measured FPS. */
export function detectQuality(renderer: THREE.WebGLRenderer): QualityLevel {
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const gl = renderer.getContext();
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const gpu = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '';
  if (/swiftshader|llvmpipe|software/i.test(gpu)) return 'low';
  if (coarse) return 'low';
  if (window.devicePixelRatio > 1.5 && screen.width * screen.height > 3_000_000) return 'medium';
  return 'high';
}

/** WebGL renderer + optional bloom post-processing with quality presets. */
export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private renderPass: RenderPass | null = null;
  quality: QualityLevel = 'high';
  private width = 1;
  private height = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Scene statistics are accumulated over all passes and reset manually once per frame.
    this.renderer.info.autoReset = false;
  }

  get preset(): Preset {
    return PRESETS[this.quality];
  }

  setQuality(q: QualityLevel, scene: THREE.Scene, camera: THREE.Camera): void {
    this.quality = q;
    const p = PRESETS[q];
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, p.pixelRatio));
    this.renderer.shadowMap.enabled = p.shadows;
    this.renderer.shadowMap.needsUpdate = true;
    scene.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (m) (Array.isArray(m) ? m : [m]).forEach((x) => (x.needsUpdate = true));
    });
    this.composer?.dispose();
    this.composer = null;
    this.bloom = null;
    if (p.bloom) {
      const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
      const target = new THREE.WebGLRenderTarget(size.x, size.y, {
        type: THREE.HalfFloatType,
        samples: p.msaa,
      });
      this.composer = new EffectComposer(this.renderer, target);
      this.renderPass = new RenderPass(scene, camera);
      this.composer.addPass(this.renderPass);
      this.bloom = new UnrealBloomPass(new THREE.Vector2(this.width, this.height), 0.55, 0.5, 0.82);
      this.composer.addPass(this.bloom);
      this.composer.addPass(new OutputPass());
    }
    this.resize(this.width, this.height);
  }

  resize(w: number, h: number): void {
    this.width = Math.max(1, w);
    this.height = Math.max(1, h);
    this.renderer.setSize(this.width, this.height, false);
    if (this.composer) {
      this.composer.setPixelRatio(this.renderer.getPixelRatio());
      this.composer.setSize(this.width, this.height);
    }
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    this.renderer.info.reset();
    if (this.composer) this.composer.render();
    else this.renderer.render(scene, camera);
  }

  info(): { calls: number; triangles: number; geometries: number; textures: number } {
    const i = this.renderer.info;
    return {
      calls: i.render.calls,
      triangles: i.render.triangles,
      geometries: i.memory.geometries,
      textures: i.memory.textures,
    };
  }

  dispose(): void {
    this.composer?.dispose();
    this.renderer.dispose();
  }
}
